import React, { useState, useEffect } from 'react';
import { Shield, Zap, Search, Server, X, ArrowRight, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'rvb_welcomed_v1';

const WelcomeModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  if (!open) return null;

  const steps = [
    {
      icon: <div className="flex items-center justify-center"><Shield className="h-10 w-10 text-blue-500 mr-1" /><Zap className="h-10 w-10 text-red-500 -ml-5" /></div>,
      title: 'Welcome to RedVsBlue',
      body: (
        <>
          <p className="text-gray-600 mb-3">
            RedVsBlue is an interactive reference of cybersecurity tools used by both sides of the security industry — the attackers and the defenders.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <div className="flex items-center font-semibold text-red-700 mb-1"><Zap size={14} className="mr-1" /> Red Team</div>
              <p className="text-gray-600">Offensive security professionals who simulate real-world attacks to find vulnerabilities.</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center font-semibold text-blue-700 mb-1"><Shield size={14} className="mr-1" /> Blue Team</div>
              <p className="text-gray-600">Defensive security professionals who protect, detect, and respond to security threats.</p>
            </div>
          </div>
        </>
      ),
    },
    {
      icon: <Search className="h-10 w-10 text-purple-500" />,
      title: 'Search & Explore Tools',
      body: (
        <>
          <p className="text-gray-600 mb-4">
            Use the search bar to instantly find any tool, technique, or attack vector across the entire database.
          </p>
          <div className="bg-stone-100 rounded-lg p-4 border border-stone-200 text-sm space-y-2">
            <p className="font-medium text-gray-700">Try searching for:</p>
            <ul className="space-y-1 text-gray-600">
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> A tool name like <span className="font-mono mx-1 text-purple-700">Metasploit</span></li>
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> A technique like <span className="font-mono mx-1 text-purple-700">ransomware</span></li>
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> A domain like <span className="font-mono mx-1 text-purple-700">cloud</span> or <span className="font-mono mx-1 text-purple-700">AI</span></li>
            </ul>
          </div>
        </>
      ),
    },
    {
      icon: <Server className="h-10 w-10 text-purple-500" />,
      title: 'Attack Scenarios',
      body: (
        <>
          <p className="text-gray-600 mb-4">
            The <strong>Attack Scenarios</strong> tab shows real-world attack chains — with the Red Team tools used to execute them alongside the Blue Team defenses that detect and stop them.
          </p>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 text-sm">
            <p className="font-medium text-purple-700 mb-2">11 scenarios including:</p>
            <ul className="space-y-1 text-gray-600">
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> Ransomware with Double Extortion</li>
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> Supply Chain & Cloud Infrastructure Attacks</li>
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> AI/ML System Compromise</li>
              <li className="flex items-center"><ChevronRight size={14} className="mr-1 text-purple-400" /> Agentic AI System Exploitation</li>
            </ul>
          </div>
        </>
      ),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-stone-50 rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-red-500 via-purple-500 to-blue-500" />

        <div className="p-6">
          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          {/* Step indicator */}
          <div className="flex justify-center gap-1.5 mb-5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-purple-500' : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-4">{current.icon}</div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 text-center mb-4">{current.title}</h2>

          {/* Body */}
          <div className="mb-6">{current.body}</div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={dismiss}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip intro
            </button>
            <button
              onClick={isLast ? dismiss : () => setStep(s => s + 1)}
              className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
            >
              {isLast ? 'Explore tools' : 'Next'}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
